import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import box from '~/assets/icons/box_2.svg';
import plus from '~/assets/icons/plus_3.svg';
import { playSound, Sound } from '~/assets/sounds';
import { CurrencyIcon } from '~/components/collections';
import {
	Button,
	ButtonBorderRadius,
	ButtonColors,
	ButtonHeight,
} from '~/components/ui/Button';
import { Divider } from '~/components/ui/Divider';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';
import { rage, useObservable } from '~/rage';
import { Currency, isClothesItem, RewardType, WalletType } from '~/shared';
import {
	setIsOpeningCase,
	setShopKitOpen,
	setShopOpenCaseVisible,
	setShopPlayerItemsOpen,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { ButtonMenu } from '../ButtonMenu';
import { ShopCases } from '../ShopCases';
import { ShopClothes } from '../ShopClothes';
import { ShopDonate } from '../ShopDonate';
import { ShopKitOpen } from '../ShopKitOpen';
import { ShopKits } from '../ShopKits';
import { ShopMain } from '../ShopMain';
import { ShopOpenCase } from '../ShopOpenCase';
import { ShopPlayer } from '../ShopPlayer';
import { ShopPlayerItems } from '../ShopPlayerItems';
import { ShopVehicle } from '../ShopVehicle';
import { ShopMenu, shopMenuIcons, shopMenuNames } from './Shop.model';
import {
	StyledContainer,
	StyledCount,
	StyledMenu,
	StyledMenuButtonContent,
	StyledMenuDonateButton,
	StyledMenuDonateContainer,
	StyledMenuDonateCount,
	StyledMenuLeft,
	StyledMenuRightContainer,
	StyledMenuTabsWrapper,
} from './Shop.styled';

const Shop = () => {
	const dispatch = useLayoutDispatch();
	const donateBalance = useObservable(rage.bank.getBalance(WalletType.Donate));
	const [activeMenuItem, setActiveMenuItem] = useState(ShopMenu.Discount);
	const donateIcon = useWalletIcon(Currency.Donate, true);
	const shopKitOpen = useLayoutSelector((state) => state.shop.shopKitOpen);
	const shopPlayerItemsOpen = useLayoutSelector(
		(state) => state.shop.shopPlayerItemsOpen,
	);
	const shopOpenCaseVisible = useLayoutSelector(
		(state) => state.shop.shopOpenCaseVisible,
	);
	const isOpeningCase = useLayoutSelector(
		(state) => state.mainMenu.isOpeningCase,
	);
	const donateItemsCount = useObservable(rage.inventoryDonate.getItemsCount());
	const inventoryShopItems = useObservable(rage.donate.getInventoryShopItems());

	const [windowWidth, setWindowWidth] = useState(window.innerWidth);
	const menuTabsWrapperRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const handleResize = () => {
			setWindowWidth(window.innerWidth);
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const menuSelected = useMemo(() => {
		if (shopKitOpen || shopPlayerItemsOpen || shopOpenCaseVisible) {
			return false;
		}

		return true;
	}, [shopKitOpen, shopPlayerItemsOpen, shopOpenCaseVisible]);

	const specialComponentVisible = useMemo(() => {
		return shopKitOpen || shopPlayerItemsOpen || shopOpenCaseVisible;
	}, [shopKitOpen, shopPlayerItemsOpen, shopOpenCaseVisible]);

	const hasClothesItems = useMemo(
		() =>
			inventoryShopItems.some((item) => {
				const rewardType = rage.inventoryDonate.getTypeById(item.donateItemId);

				return (
					rewardType === RewardType.ITEM && isClothesItem(item.donateItemId)
				);
			}),
		[inventoryShopItems],
	);

	const hasVehicleItems = useMemo(
		() =>
			inventoryShopItems.some((item) => {
				const rewardType = rage.inventoryDonate.getTypeById(item.donateItemId);

				return rewardType === RewardType.VEHICLE;
			}),
		[inventoryShopItems],
	);

	const menuItems = useMemo(
		() =>
			Object.values(ShopMenu).filter((item) => {
				if (item === ShopMenu.Clothes) {
					return hasClothesItems;
				}

				if (item === ShopMenu.Vehicle) {
					return hasVehicleItems;
				}

				return true;
			}),
		[hasClothesItems, hasVehicleItems],
	);

	useEffect(() => {
		if (!hasClothesItems && activeMenuItem === ShopMenu.Clothes) {
			setActiveMenuItem(ShopMenu.Discount);
			return;
		}

		if (!hasVehicleItems && activeMenuItem === ShopMenu.Vehicle) {
			setActiveMenuItem(ShopMenu.Discount);
		}
	}, [activeMenuItem, hasClothesItems, hasVehicleItems]);

	const activeMenuIndex = menuSelected ? menuItems.indexOf(activeMenuItem) : -1;

	useEffect(() => {
		if (activeMenuIndex < 0) return;
		const wrapper = menuTabsWrapperRef.current;
		if (!wrapper) return;
		const activeButton = wrapper.children[activeMenuIndex] as HTMLElement;
		if (activeButton) {
			activeButton.scrollIntoView({
				behavior: 'smooth',
				block: 'nearest',
				inline: 'center',
			});
		}
	}, [activeMenuIndex]);

	const getMenuLabel = (item: ShopMenu) => {
		if (item === ShopMenu.Discount && windowWidth <= 1500) {
			return 'Акции';
		}
		return shopMenuNames[item];
	};

	return (
		<StyledContainer>
			<StyledMenu>
				<StyledMenuLeft>
					<ButtonMenu
						buttons={menuItems.map((item) => ({
							label: getMenuLabel(item),
							icon: shopMenuIcons[item],
						}))}
						activeIndex={activeMenuIndex}
						setActiveIndex={(index) => {
							if (index < 0 || index >= menuItems.length) {
								return;
							}

							setActiveMenuItem(menuItems[index]);
							if (shopKitOpen) {
								dispatch(setShopKitOpen(false));
							}
							if (shopPlayerItemsOpen) {
								dispatch(setShopPlayerItemsOpen(false));
							}
							if (shopOpenCaseVisible) {
								dispatch(setShopOpenCaseVisible(false));
								dispatch(setIsOpeningCase(false));
							}
						}}
						buttonsWrapper={StyledMenuTabsWrapper}
						buttonsWrapperRef={menuTabsWrapperRef}
					/>
				</StyledMenuLeft>
				<StyledMenuRightContainer>
					<Button
						color={
							shopPlayerItemsOpen ? ButtonColors.Select : ButtonColors.Default
						}
						height={ButtonHeight.Height40}
						borderRadius={ButtonBorderRadius.Radius5}
						onClick={() => {
							if (isOpeningCase) {
								return;
							}
							dispatch(setShopPlayerItemsOpen(true));
							dispatch(setShopKitOpen(false));
							dispatch(setShopOpenCaseVisible(false));
						}}
					>
						<StyledMenuButtonContent>
							<img src={box} />
							<Text>Мои предметы</Text>
							{donateItemsCount > 0 && (
								<StyledCount>
									<Text>{donateItemsCount > 9 ? '∞' : donateItemsCount}</Text>
								</StyledCount>
							)}
						</StyledMenuButtonContent>
					</Button>
					<Divider $direction="V" />
					<StyledMenuDonateContainer>
						<StyledMenuDonateCount>
							<CurrencyIcon icon={donateIcon} size="md" />
							<Text>{donateBalance.toLocaleString('ru-RU')}</Text>
						</StyledMenuDonateCount>
						<StyledMenuDonateButton
							onMouseEnter={() => playSound(Sound.LinkCursor)}
							onClick={() => {
								window.open('https://ccdplanet.com/', '_blank');
								playSound(Sound.ClickButton);
							}}
						>
							<img src={plus} />
						</StyledMenuDonateButton>
					</StyledMenuDonateContainer>
				</StyledMenuRightContainer>
			</StyledMenu>
			{shopKitOpen && <ShopKitOpen />}
			{shopPlayerItemsOpen && <ShopPlayerItems />}
			{shopOpenCaseVisible && <ShopOpenCase />}
			{!specialComponentVisible && (
				<Fragment>
					{activeMenuItem === ShopMenu.Discount && <ShopMain />}
					{activeMenuItem === ShopMenu.Cases && <ShopCases />}
					{activeMenuItem === ShopMenu.Donate && <ShopDonate />}
					{activeMenuItem === ShopMenu.Player && <ShopPlayer />}
					{activeMenuItem === ShopMenu.Kits && <ShopKits />}
					{activeMenuItem === ShopMenu.Clothes && <ShopClothes />}
					{activeMenuItem === ShopMenu.Vehicle && <ShopVehicle />}
				</Fragment>
			)}
		</StyledContainer>
	);
};

export { Shop };
